import { AnyObjectSchema } from 'yup';
import { isValidNeglectedOrDelinquentCount } from '../../utilities/neglectedOrDelinquentUtilities';
import { YES_NO_OPTIONS } from '../Constants';
import { SelectOption } from '../SelectOption';
import { VALIDATION_MESSAGE } from '../ValidationMessage';
import { DIPLOMA_FIELDS } from '../NeglectedOrDelinquentHighSchoolDiplomaFields';
import { GED_SECTIONS } from '../NeglectedOrDelinquentGEDFields';
import { PERIOD_COUNT_FIELDS } from '../NeglectedOrDelinquentPeriodCountFields';
import {
    STUDENT_CHARACTERISTIC_FIELDS,
} from '../NeglectedOrDelinquentStudentCharacteristicsFields';
import {
    AGE_FIELDS,
    GENDER_FIELDS,
    RACE_ETHNICITY_FIELDS,
} from '../NeglectedOrDelinquentStudentDemographicsFields';
import yup from './CustomValidation/yup-extended';

const studentPopulationWholeNumber =
    yup.string()
        .required(VALIDATION_MESSAGE.REQUIRED)
        .mustBeNumber()
        .max(6, VALIDATION_MESSAGE.AT_MOST_CHARS(6));

const raceTotalMessage =
    'Total Students by Race/Ethnicity must equal Unduplicated Students Served.';
const genderTotalMessage =
    'Total Students by Gender must equal Unduplicated Students Served.';
const ageTotalMessage =
    'Total Students by Age must equal Unduplicated Students Served.';
const characteristicsLimitMessage =
    'Number of students in any of the Student Characteristic fields must be equal to ' +
    'or less than Unduplicated Students Served.';
const courseCreditsLimitMessage =
    'Number must be equal to or less than Unduplicated Students Served.';
const diplomaTotalLimitMessage =
    'Total Students Awarded Diplomas must be equal to or less than ' +
    'Unduplicated Students Served.';

const courseCreditsCount = studentPopulationWholeNumber.when('awardedCourseCredits', {
    is: 'Yes',
    otherwise: yup.string(),
});
const diplomaCount = studentPopulationWholeNumber.when('awardedHighSchoolDiploma', {
    is: 'Yes',
    otherwise: yup.string(),
});

const countExceedsPopulation = (count: unknown, population: unknown): boolean =>
    isValidNeglectedOrDelinquentCount(count)
    && isValidNeglectedOrDelinquentCount(population)
    && Number(count) > Number(population);

const createCountFieldsSchema = (
    fields: { key: string }[]
): Record<string, ReturnType<typeof studentPopulationWholeNumber.clone>> =>
    Object.fromEntries(fields.map(({ key }) => [
        key,
        yup.string()
            .required(VALIDATION_MESSAGE.REQUIRED)
            .mustBeNumber()
            .max(6, VALIDATION_MESSAGE.AT_MOST_CHARS(6)),
    ]));

const demographicGroupTotal = (
    group: Record<string, unknown> | undefined,
    fields: { key: string }[]
): number | null => {
    if (!group) return null;
    const values = fields.map(({ key }) => group[key]);
    if (!values.every(isValidNeglectedOrDelinquentCount)) {
        return null;
    }
    return values.reduce<number>((total, value) => total + Number(value), 0);
};

const totalMatchesPopulation = (
    group: Record<string, unknown> | undefined,
    fields: { key: string }[],
    population: unknown
): boolean => {
    if (!isValidNeglectedOrDelinquentCount(population)) return true;
    const total = demographicGroupTotal(group, fields);
    return total === null || total === Number(population);
};

export const createNeglectedOrDelinquentInformationValidationSchema = (
    facilityTypes: SelectOption[],
    isStateOperatedAgency = false
): AnyObjectSchema =>
    yup.object().shape({
        unduplicatedStudentsServed: studentPopulationWholeNumber,
        totalYearlyLengthOfStayDays: studentPopulationWholeNumber,
        fundingReceived1: isStateOperatedAgency
            ? yup.string().oneOf(YES_NO_OPTIONS.map(({ value }) => value))
                .required(VALIDATION_MESSAGE.REQUIRED)
            : yup.string(),
        fundingReceived2: !isStateOperatedAgency
            ? yup.string().oneOf(YES_NO_OPTIONS.map(({ value }) => value))
                .required(VALIDATION_MESSAGE.REQUIRED)
            : yup.string(),
        facilityType: yup.string().when(['fundingReceived1', 'fundingReceived2'], {
            is: (funding1: string, funding2: string) =>
                isStateOperatedAgency ? funding1 === 'Yes' : funding2 === 'Yes',
            then: yup
                .string()
                .required(VALIDATION_MESSAGE.REQUIRED)
                .oneOf(
                    facilityTypes
                        .filter((option) => !option.isDisabled)
                        .map((option) => option.value),
                    'Select an available facility type.'
                ),
        }),
        studentDemographics: yup.object().shape({
            raceEthnicity: yup.object().shape(
                createCountFieldsSchema(RACE_ETHNICITY_FIELDS)
            ),
            gender: yup.object().shape(
                createCountFieldsSchema(GENDER_FIELDS)
            ),
            age: yup.object().shape(
                createCountFieldsSchema(AGE_FIELDS)
            ),
        }),
        studentCharacteristics: yup.object().shape(
            createCountFieldsSchema(STUDENT_CHARACTERISTIC_FIELDS)
        ),
        highSchoolCourseCredits: yup.object().shape({
            awardedCourseCredits: yup.string()
                .oneOf(YES_NO_OPTIONS.map(({ value }) => value))
                .required(VALIDATION_MESSAGE.REQUIRED),
            studentsEarnedCreditsWhileInProgram: courseCreditsCount,
            studentsEarnedCreditsWithin90DaysOfExiting: courseCreditsCount,
        }),
        highSchoolDiploma: yup.object().shape({
            awardedHighSchoolDiploma: yup.string()
                .oneOf(YES_NO_OPTIONS.map(({ value }) => value))
                .required(VALIDATION_MESSAGE.REQUIRED),
            studentsAwardedDiplomaWhileInProgram: diplomaCount,
            studentsAwardedDiplomaWithin90DaysOfExiting: diplomaCount,
        }),
        ged: yup.object().shape({
            enrolledInGEDProgram: yup.object().shape(
                createCountFieldsSchema(PERIOD_COUNT_FIELDS)
            ),
            earnedGED: yup.object().shape(
                createCountFieldsSchema(PERIOD_COUNT_FIELDS)
            ),
        }),
        postSecondaryEducation: yup.object().shape(
            createCountFieldsSchema(PERIOD_COUNT_FIELDS)
        ),
        jobTraining: yup.object().shape(
            createCountFieldsSchema(PERIOD_COUNT_FIELDS)
        ),
        employment: yup.object().shape(
            createCountFieldsSchema(PERIOD_COUNT_FIELDS)
        ),
        schoolReEnrollment: yup.object().shape({
            studentsReturnedWithin90DaysOfExiting: studentPopulationWholeNumber,
        }),
    })
        .test(
            'race-ethnicity-total',
            raceTotalMessage,
            function (values) {
                const valid = totalMatchesPopulation(
                    values?.studentDemographics?.raceEthnicity,
                    RACE_ETHNICITY_FIELDS,
                    values?.unduplicatedStudentsServed
                );
                return valid || this.createError({
                    path: 'raceEthnicityTotal',
                    message: raceTotalMessage,
                });
            }
        )
        .test(
            'gender-total',
            genderTotalMessage,
            function (values) {
                const valid = totalMatchesPopulation(
                    values?.studentDemographics?.gender,
                    GENDER_FIELDS,
                    values?.unduplicatedStudentsServed
                );
                return valid || this.createError({
                    path: 'genderTotal',
                    message: genderTotalMessage,
                });
            }
        )
        .test(
            'age-total',
            ageTotalMessage,
            function (values) {
                const valid = totalMatchesPopulation(
                    values?.studentDemographics?.age,
                    AGE_FIELDS,
                    values?.unduplicatedStudentsServed
                );
                return valid || this.createError({
                    path: 'ageTotal',
                    message: ageTotalMessage,
                });
            }
        )
        .test(
            'student-characteristics-limit',
            characteristicsLimitMessage,
            function (values) {
                const population = values?.unduplicatedStudentsServed;
                if (!isValidNeglectedOrDelinquentCount(population)) {
                    return true;
                }
                const exceedsPopulation = STUDENT_CHARACTERISTIC_FIELDS.some(
                    ({ key }) => {
                        const count = values?.studentCharacteristics?.[key];
                        return isValidNeglectedOrDelinquentCount(count)
                            && Number(count) > Number(population);
                    }
                );
                return !exceedsPopulation || this.createError({
                    path: 'studentCharacteristicsLimit',
                    message: characteristicsLimitMessage,
                });
            }
        )
        .test(
            'course-credits-while-limit',
            courseCreditsLimitMessage,
            function (values) {
                const credits = values?.highSchoolCourseCredits;
                return credits?.awardedCourseCredits !== 'Yes' ||
                    !countExceedsPopulation(
                        credits.studentsEarnedCreditsWhileInProgram,
                        values?.unduplicatedStudentsServed
                    ) || this.createError({
                        path: 'courseCreditsWhileLimit',
                        message: courseCreditsLimitMessage,
                    });
            }
        )
        .test(
            'course-credits-exit-limit',
            courseCreditsLimitMessage,
            function (values) {
                const credits = values?.highSchoolCourseCredits;
                return credits?.awardedCourseCredits !== 'Yes' ||
                    !countExceedsPopulation(
                        credits.studentsEarnedCreditsWithin90DaysOfExiting,
                        values?.unduplicatedStudentsServed
                    ) || this.createError({
                        path: 'courseCreditsExitLimit',
                        message: courseCreditsLimitMessage,
                    });
            }
        )
        .test(
            'diploma-total-limit',
            diplomaTotalLimitMessage,
            function (values) {
                const diploma = values?.highSchoolDiploma;
                const population = values?.unduplicatedStudentsServed;
                if (diploma?.awardedHighSchoolDiploma !== 'Yes' ||
                    !isValidNeglectedOrDelinquentCount(population)) {
                    return true;
                }
                const total = demographicGroupTotal(diploma, DIPLOMA_FIELDS);
                return total === null || total <= Number(population) ||
                    this.createError({
                        path: 'diplomaTotalLimit',
                        message: diplomaTotalLimitMessage,
                    });
            }
        )
        .test(
            'ged-total-limits',
            function (values) {
                const population = values?.unduplicatedStudentsServed;
                if (!isValidNeglectedOrDelinquentCount(population)) return true;

                const errors = GED_SECTIONS.flatMap(({ key, totalLabel, totalField }) => {
                    const total = demographicGroupTotal(
                        values?.ged?.[key], PERIOD_COUNT_FIELDS
                    );
                    return total === null || total <= Number(population) ? [] : [
                        this.createError({
                            path: totalField,
                            message: `${totalLabel} must be equal to or less than ` +
                                'Unduplicated Students Served.',
                        }),
                    ];
                });
                return errors.length === 0 || new yup.ValidationError(errors);
            }
        )
        .test(
            'post-secondary-limits',
            function (values) {
                const errors = PERIOD_COUNT_FIELDS.flatMap(({ key }) =>
                    countExceedsPopulation(
                        values?.postSecondaryEducation?.[key],
                        values?.unduplicatedStudentsServed
                    ) ? [
                        this.createError({
                            path: key === 'whileInProgram'
                                ? 'postSecondaryWhileLimit' : 'postSecondaryExitLimit',
                            message: courseCreditsLimitMessage,
                        }),
                    ] : []
                );
                return errors.length === 0 || new yup.ValidationError(errors);
            }
        )
        .test(
            'job-training-limits',
            function (values) {
                const errors = PERIOD_COUNT_FIELDS.flatMap(({ key }) =>
                    countExceedsPopulation(
                        values?.jobTraining?.[key],
                        values?.unduplicatedStudentsServed
                    ) ? [
                        this.createError({
                            path: key === 'whileInProgram'
                                ? 'jobTrainingWhileLimit' : 'jobTrainingExitLimit',
                            message: courseCreditsLimitMessage,
                        }),
                    ] : []
                );
                return errors.length === 0 || new yup.ValidationError(errors);
            }
        )
        .test(
            'employment-limits',
            function (values) {
                const errors = PERIOD_COUNT_FIELDS.flatMap(({ key }) =>
                    countExceedsPopulation(
                        values?.employment?.[key],
                        values?.unduplicatedStudentsServed
                    ) ? [
                        this.createError({
                            path: key === 'whileInProgram'
                                ? 'employmentWhileLimit' : 'employmentExitLimit',
                            message: courseCreditsLimitMessage,
                        }),
                    ] : []
                );
                return errors.length === 0 || new yup.ValidationError(errors);
            }
        )
        .test(
            'school-re-enrollment-limit',
            function (values) {
                return !countExceedsPopulation(
                    values?.schoolReEnrollment?.studentsReturnedWithin90DaysOfExiting,
                    values?.unduplicatedStudentsServed
                ) || this.createError({
                    path: 'schoolReEnrollmentLimit',
                    message: 'Number of students must be equal to or less than ' +
                        'Unduplicated Students Served.',
                });
            }
        );
